import { t } from 'i18next';
import { replace } from 'lodash';
import * as yup from 'yup';

import i18n from '../../../i18n';
import { sentenceCase } from '../../../utils/utils';
import { instanceSchema } from '../instance-stepper/schema';

export const instanceLabels = (clusterType) => {
  const selectedType = {
    SOLR_CLUSTER: {
      title: t('editInstance', { ns: 'sectionTitles' }),
      name: t('instanceName', { ns: 'fields' }),
      description: t('instanceDesc', { ns: 'fields' }),
      subdomain: t('instanceSubdomain', { ns: 'fields' }),
    },
    GPS_CLUSTER: {
      title: t('editServer', { ns: 'sectionTitles' }),
      name: t('instanceName', { ns: 'fields' }),
      description: t('serverDesc', { ns: 'fields' }),
      subdomain: t('serverSubdomain', { ns: 'fields' }),
    },
    MODEL_CLUSTER: {
      title: t('editModel', { ns: 'sectionTitles' }),
      name: t('modelName', { ns: 'fields' }),
      description: t('modelDesc', { ns: 'fields' }),
      subdomain: t('modelSubdomain', { ns: 'fields' }),
    },
    CUSTOM_CLUSTER: {
      title: t('editCustomInst', { ns: 'sectionTitles' }),
      name: t('customInstName', { ns: 'fields' }),
      description: t('customInstDesc', { ns: 'fields' }),
    },
    ASSISTANT_CLUSTER: {
      title: t('instance', { ns: 'assistant', count: 1 }),
      name: t('instanceName', { ns: 'fields' }),
      description: t('instanceDesc', { ns: 'fields' }),
      subdomain: t('instanceSubdomain', { ns: 'fields' }),
    },
  };

  return selectedType[clusterType];
};

export const schema = (checkDuplicate) =>
  yup.object().shape({
    icon: instanceSchema().icon,
    name: instanceSchema().name,
    description: instanceSchema().description,
    subdomain: instanceSchema().subdomain.test(
      'isUnique',
      sentenceCase(
        t('duplicate', {
          ns: 'validations',
          field: t('instanceSubdomain', { ns: 'fields' }),
        })
      ),
      async (value) => {
        const { isValid } = await checkDuplicate(value);
        return isValid;
      }
    ),
    cidrs: instanceSchema().cidrs,
    clusterType: yup.string(),
  });

// Allows any ip address with mask between 0 and 32
export const IP_RANGE_FORMAT_VALIDATION =
  /^([0-9]{1,3}\.){3}[0-9]{1,3}\/([0-9]|1[0-9]|2[0-9]|30|31|32)$/g;

export const cidrSchema = (cidrs) =>
  yup.object().shape({
    cidr: yup
      .string()
      .required(
        sentenceCase(
          i18n.t('inputRequired', {
            ns: 'validations',
            field: i18n.t('ipRange', { ns: 'fields' }),
          })
        )
      )
      .matches(
        IP_RANGE_FORMAT_VALIDATION,
        sentenceCase(
          `${i18n.t('invalidFormat', {
            ns: 'validations',
            field: i18n.t('ipRange', { ns: 'fields' }),
          })} ${i18n.t('example', { ns: 'validations', example: '0.0.0.0/0' })}`
        )
      )
      .test(
        'isUnique',
        sentenceCase(
          i18n.t('duplicate', {
            ns: 'validations',
            field: i18n.t('ipRange', { ns: 'fields' }),
          })
        ),
        (value) => cidrs.indexOf(value) < 0
      )
      .test(
        'isValidCIDR',
        sentenceCase(
          i18n.t('invalidFormat', {
            ns: 'validations',
            field: i18n.t('ipRange', { ns: 'fields' }),
          })
        ),
        function (value) {
          try {
            if (value === undefined) return false;

            const [ip, mask] = value.split('/');
            const numbers = ip.split('.');

            // ip to binary
            let binaryIpNumbers = '';

            for (let i = 0; i < numbers.length; i++) {
              const binaryIpNumber = Number(numbers[i]).toString(2);

              for (let j = binaryIpNumber.length; j < 8; j++)
                binaryIpNumbers += '0';
              binaryIpNumbers += binaryIpNumber;
            }

            // validate mask
            const endIp = binaryIpNumbers.substr(mask);
            const isValid = endIp.indexOf('1') < 0;

            // suggested ip address
            const suggestedIpAddress = `${(
              binaryIpNumbers.substr(0, mask) + replace(endIp, /1/g, '0')
            )
              .match(/.{8}/g)
              .map((binary) => parseInt(binary, 2))
              .join('.')}/${mask}`;

            return (
              isValid ||
              this.createError({
                message: sentenceCase(
                  `${i18n.t('invalidFormat', {
                    ns: 'validations',
                    field: i18n.t('ipRange', { ns: 'fields' }),
                  })} ${i18n.t('example', {
                    ns: 'validations',
                    example: suggestedIpAddress,
                  })}`
                ),
              })
            );
          } catch (e) {
            return false;
          }
        }
      ),
  });
